import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ExternalLink, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const mdComponents = {
  h1: ({ children }) => <h1 className="text-4xl sm:text-5xl font-serif text-forest leading-tight mb-6">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-forest mt-12 mb-4 pb-2 border-b border-cream-dark">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-forest mt-8 mb-3">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-forest mt-6 mb-2">{children}</h4>,
  p: ({ children }) => <p className="text-sm md:text-base text-forest/80 leading-relaxed mb-4">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1 text-sm md:text-base text-forest/80">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1 text-sm md:text-base text-forest/80">{children}</ol>,
  a: ({ href, children }) => href?.startsWith("/")
    ? <Link to={href} className="text-brand underline-offset-2 hover:underline">{children}</Link>
    : <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand underline-offset-2 hover:underline">{children}</a>,
  table: ({ children }) => <div className="overflow-x-auto mb-6 rounded-xl border border-cream-dark"><table className="w-full text-sm">{children}</table></div>,
  th: ({ children }) => <th className="text-left bg-cream px-4 py-2 font-semibold text-forest">{children}</th>,
  td: ({ children }) => <td className="px-4 py-3 align-top border-t border-cream-dark text-forest/80 [&_strong]:text-forest">{children}</td>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-brand bg-brand/5 px-4 py-3 rounded-r-lg my-6 text-sm [&_p]:mb-0">{children}</blockquote>,
  hr: () => <hr className="my-10 border-cream-dark" />,
};

const dialogMd = { ...mdComponents, h1: ({ children }) => <h1 className="text-2xl font-serif text-forest leading-tight mb-4">{children}</h1> };

export const LegalDialog = ({ slug, title, open, onOpenChange }) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !slug) return;
    setDoc(null); setError(false);
    axios.get(`${API}/legal/${slug}`, { withCredentials: true }).then(r => setDoc(r.data)).catch(() => setError(true));
  }, [open, slug]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100%-2rem)] max-h-[85vh] flex flex-col p-0 gap-0" data-testid="legal-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-cream-dark text-left">
          <DialogTitle className="font-serif text-forest">{title}</DialogTitle>
          <DialogDescription className="text-xs text-sage flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />Titolare: Melaverde Srl{doc?.updated_at ? ` · aggiornato al ${doc.updated_at}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4 flex-1 min-h-0" data-testid="legal-dialog-content">
          {error && <p className="text-sage">Documento non disponibile al momento.</p>}
          {!doc && !error && <div className="animate-pulse space-y-3"><div className="h-6 bg-cream rounded w-2/3" /><div className="h-4 bg-cream rounded" /><div className="h-4 bg-cream rounded w-5/6" /></div>}
          {doc && <ReactMarkdown remarkPlugins={[remarkGfm]} components={dialogMd}>{doc.content}</ReactMarkdown>}
        </div>
        <div className="px-6 py-3 border-t border-cream-dark flex items-center justify-between gap-3 bg-cream/40">
          <a href={`/privacy/${slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-sage hover:text-forest inline-flex items-center gap-1" data-testid="legal-dialog-open-tab">
            <ExternalLink className="h-3 w-3" />Apri in una nuova scheda
          </a>
          <Button onClick={() => onOpenChange(false)} className="bg-brand text-white hover:bg-brand-dark" data-testid="legal-dialog-close">Ho letto, chiudi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
